import { useEffect } from 'react';
import { useLocation, useMatches } from 'react-router';

import { warmRouteData } from '~/modules/root/util/warm-route-data';

/** Lets rapid staging churn settle before spending a request on a warm. */
const WARM_DELAY_MS = 300;

/**
 * Warms the route data for `page` (see warmRouteData) while it stands staged,
 * so a subsequent Apply serves from cache.
 *
 * The `_routes` list mirrors this app's revalidation policy: root never
 * revalidates; the layouts skip search-only changes but revalidate on
 * pathname changes (a staged category commit).
 */
export const StagedDestinationWarmer = ({ page }: { page: string }) => {
    const location = useLocation();
    const matches = useMatches();
    // Everything below root, in match order — RR joins _routes in that order.
    const revalidatingRouteIds = (
        new URL(page, 'https://x').pathname === location.pathname
            ? [matches[matches.length - 1]?.id]
            : matches.slice(1).map((match) => match.id)
    )
        .filter((id): id is string => !!id)
        .join(',');

    useEffect(() => {
        if (!revalidatingRouteIds) return;

        const timeout = setTimeout(() => {
            warmRouteData(page, revalidatingRouteIds);
        }, WARM_DELAY_MS);
        return () => clearTimeout(timeout);
    }, [page, revalidatingRouteIds]);

    return null;
};
